import { useState, useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';

import arrow from './arrow-down.svg';

import styles from './Dropdown.module.scss';

interface DropdownProps<T> {
  options: { name: string; value: T }[];
  setValue: (val: T) => void;
  id: string;
  styleSet: { wrapper: string; options: string };
  renderSelected: (selected: string) => React.ReactElement;
  placeholder?: string;
  value?: T;
  readonly?: boolean;
}

function Dropdown<T>({
  options,
  value,
  id,
  setValue,
  placeholder = '',
  styleSet,
  renderSelected,
  readonly = false
}: DropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(
    () => options.find((it) => it.value === value)?.name ?? '',
    [options, value]
  );

  const onSelect = (value: T) => {
    setOpen(false);
    setValue(value);
  };

  const onClickOutsideHandler = useCallback(
    (e: MouseEvent) => {
      if (open && !document.getElementById(id)!.contains(e.target as Node)) {
        setOpen(false);
      }
    },
    [open, id]
  );

  useEffect(() => {
    document.addEventListener('click', onClickOutsideHandler);

    return () => {
      document.removeEventListener('click', onClickOutsideHandler);
    };
  }, [onClickOutsideHandler]);

  if (readonly) {
    return (
      <div
        id={id}
        className={classNames(styleSet.wrapper, styles.readonly)}
        onClick={(e) => e.stopPropagation()}>
        <div>{renderSelected(selected || placeholder)}</div>
      </div>
    );
  }

  return (
    <div id={id} className={styleSet.wrapper} onClick={(e) => e.stopPropagation()}>
      <div onClick={() => setOpen((wasOpen) => !wasOpen)}>
        {renderSelected(selected || placeholder)}
      </div>
      {open && (
        <ul className={styleSet.options}>
          {options.map(({ name, value }) => (
            <li key={value as React.Key} onClick={() => onSelect(value)}>
              {name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AdminDropdown<T>(props: Omit<DropdownProps<T>, 'styleSet' | 'renderSelected'>) {
  return (
    <Dropdown
      styleSet={{ wrapper: styles.adminWrapper, options: styles.adminOptions }}
      renderSelected={(selected: string) => (
        <>
          {selected} <img src={arrow} alt="" />
        </>
      )}
      {...props}
    />
  );
}

export { AdminDropdown };
